import { format, isValid } from 'date-fns';

export const STAGE_LABELS = {
  pif_submitted: 'PIF Submitted',
  analyst_review: 'Analyst Review',
  peer_review: 'Peer Review',
  hop_review: 'HoP Review',
  iic_approval: 'IIC Approval',
  screening: 'Screening',
  iic_approval_ic1: 'IIC Sign-off',
  ic1: 'IC1',
  due_diligence: 'Due Diligence',
  iic_approval_ic2: 'IIC Approval (IC2)',
  ic2: 'IC2',
  post_ic2: 'Deployment',
  invested: 'Portfolio Management',
  suspended: 'Suspended',
  denied: 'Denied',
};

export const STAGE_ORDER = [
  'pif_submitted',
  'analyst_review',
  'peer_review',
  'hop_review',
  'iic_approval',
  'screening',
  'iic_approval_ic1',
  'ic1',
  'due_diligence',
  'iic_approval_ic2',
  'ic2',
  'post_ic2',
  'invested',
];

// Stages the workflow currently writes to current_stage. The remaining
// STAGE_ORDER entries are defined for display but are not yet reachable by
// any advanceDealStage transition (see Item #2 notes). Do NOT add
// transitions here — this is a display flag only.
export const IMPLEMENTED_STAGES = [
  'pif_submitted',
  'analyst_review',
  'peer_review',
  'hop_review',
  'iic_approval',
  'screening',
  'iic_approval_ic1',
  'due_diligence',
];

// The final step of an IIC approval chain advances the deal to a gate-specific
// stage. The pre_screening gate (live) advances to 'screening'; the ic1 gate
// (the IIC Sign-off gate) advances to 'ic1'. Values MUST be valid STAGE_ORDER
// stages — see formatters test. Keyed by iic_approvals.gate.
export const FINAL_STAGE_BY_GATE = {
  pre_screening: 'screening',
  ic1: 'ic1',
};

// Audit action label written when the final step of each gate's chain advances
// the deal. Keyed by iic_approvals.gate. pre_screening is unchanged.
export const FINAL_ADVANCE_ACTION_BY_GATE = {
  pre_screening: 'iic_approval_complete',
  ic1: 'iic_signoff_complete',
};

// Company notification type sent when the final step of each gate's chain
// ADVANCES the deal (the deny/suspend paths keep their own notifications).
// pre_screening -> screening (unchanged); ic1 -> the decision to present the
// deal at IC1 (docs/01:232). Keyed by iic_approvals.gate.
export const ADVANCE_COMPANY_NOTIFICATION_BY_GATE = {
  pre_screening: 'advancing_to_screening',
  ic1: 'presenting_at_ic1',
};

export const SECTOR_OPTIONS = [
  'Agriculture & Agribusiness',
  'Financial Services',
  'Healthcare',
  'Education',
  'Technology',
  'Energy',
  'Manufacturing',
  'Real Estate',
  'Retail & Consumer',
  'Logistics & Supply Chain',
  'Media & Entertainment',
  'Other',
];

export function formatStageLabel(stage) {
  return STAGE_LABELS[stage] || stage;
}

export function formatCurrency(amount, currencyCode) {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currencyCode || ''} ${Number(amount).toLocaleString()}`;
  }
}

export function formatDate(value, fmt = 'dd MMM yyyy') {
  if (!value) return '—';
  const d = value.toDate ? value.toDate() : new Date(value);
  return isValid(d) ? format(d, fmt) : '—';
}

export function formatDateTime(value) {
  return formatDate(value, 'dd MMM yyyy, HH:mm');
}

export function formatShortId(id) {
  if (!id) return '—';
  return id.substring(0, 8).toUpperCase();
}

export function formatPercentage(decimal, decimals = 1) {
  if (decimal === null || decimal === undefined) return '—';
  return `${(decimal * 100).toFixed(decimals)}%`;
}

// Insert thousands separators into a numeric string, preserving any decimals
// and sign exactly (no float rounding). Assumes /^-?\d+(\.\d+)?$/.
function groupThousands(numStr) {
  const neg = numStr.startsWith('-');
  const body = neg ? numStr.slice(1) : numStr;
  const [intPart, decPart] = body.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + grouped + (decPart !== undefined ? '.' + decPart : '');
}

/**
 * Display-only formatter for figure answers: adds thousands separators to a
 * numeric value (keeping any trailing unit/currency) and returns anything
 * non-numeric unchanged. NEVER use on a value that flows into parseFloat,
 * calculateScore, convertMonetaryValue, or Firestore storage — display copy
 * only. Never throws.
 * Examples:
 *   formatFigureValue(88000000)      -> "88,000,000"
 *   formatFigureValue("1100000")     -> "1,100,000"
 *   formatFigureValue("1100000 GHS") -> "1,100,000 GHS"
 *   formatFigureValue("1250.5")      -> "1,250.5"
 *   formatFigureValue("2019-03-01")  -> "2019-03-01" (unchanged)
 *   formatFigureValue("Yes")         -> "Yes" (unchanged)
 */
export function formatFigureValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') {
    return Number.isFinite(value) ? groupThousands(String(value)) : String(value);
  }
  const str = String(value).trim();
  if (str === '') return '—';
  // Pure numeric string (int or decimal, optional sign)
  if (/^-?\d+(\.\d+)?$/.test(str)) return groupThousands(str);
  // Numeric part followed by a unit/currency, e.g. "1100000 GHS"
  const m = str.match(/^(-?\d+(?:\.\d+)?)(\s+\S.*)$/);
  if (m) return groupThousands(m[1]) + m[2];
  // Non-numeric (text, date, option label) → unchanged
  return str;
}

export function roleLabel(role) {
  const labels = {
    analyst: 'Analyst',
    head_of_pipeline: 'Head of Pipeline',
    cio: 'CIO',
    md: 'MD',
    ic_member: 'IC Member',
    admin: 'Admin',
  };
  return labels[role] || role;
}

// ─── User name resolver (for version history and audit log display) ──────────

const _nameCache = {};

export async function resolveUserName(uid, db) {
  if (!uid || uid === 'system') return uid === 'system' ? 'System' : 'Unknown';
  if (_nameCache[uid]) return _nameCache[uid];
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await getDoc(doc(db, 'users', uid));
    if (snap.exists()) {
      const name = snap.data().full_name || snap.data().display_name || uid;
      _nameCache[uid] = name;
      return name;
    }
  } catch {
    // non-fatal
  }
  return uid;
}

// ─── User-friendly error messages ────────────────────────────────────────────

export function getFriendlyError(error) {
  const code = error?.code || '';
  const message = error?.message || '';

  if (code.includes('permission-denied') ||
      message.includes('Missing or insufficient')) {
    return 'Unable to complete this action. Please refresh the page and try again. ' +
      'If the problem continues contact your administrator.';
  }
  if (code.includes('not-found')) {
    return 'The requested record could not be found.';
  }
  if (code.includes('unavailable')) {
    return 'Service temporarily unavailable. Please try again in a moment.';
  }
  if (code.includes('unauthenticated')) {
    return 'Your session has expired. Please sign in again.';
  }
  return 'Something went wrong. Please try again.';
}
